import { MeetingContainerAPI, MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import { OvenMediaReceiverWebRTCSession, OvenMediaReceiverWebRTCSubscription } from "@kalyzee/kast-web-sdk/webrtc";
import { MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { useMemo, useRef } from "react";
import { useMeetingSelector } from "./meeting";
import { useStudentContext } from "../pages/master/student/context";

export const useVideoSubscriptions = () => {
  const ref = useRef<{ [id: string]: OvenMediaReceiverWebRTCSubscription }>({});

  const update = (subscriptions: {
    session: OvenMediaReceiverWebRTCSession,
    active: boolean,
  }[]) => {
    subscriptions.forEach(({ session, active }) => {
      const existing = ref.current[session.getId()];
      if (active) {
        if (!existing) {
          ref.current[session.getId()] = session.subscribe({ video: true, audio: false });
        }
      } else {
        if (existing) {
          existing.unsubscribe();
          delete ref.current[session.getId()];
        }
      }
    });
  };

  const reset = () => {
    Object.values(ref.current).forEach(subscription => subscription.unsubscribe());
    ref.current = {};
  };

  return { update, reset };
};

export type LocalOrRemoteMedia = {
  session: MeetingSession;
  mediaSession: MeetingSessionMedia;
} & (
  | { type: "local"; stream: MediaStream }
  | { type: "remote"; videoSession: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData> }
);

export const useLocalOrRemoteMedia = (
  meetingAPI?: MeetingContainerAPI,
  filter?: (session: MeetingSession) => boolean,
) => {
  const [session] = useMeetingSelector(meetingAPI, (state) => state.session);
  const [receptionSessions] = useMeetingSelector(meetingAPI, (state) => state.receptionSessions);

  const studentCtx = useStudentContext();

  return useMemo(
    () => {
      if (!filter || !meetingAPI) return [];

      const foundMedias = new Set<string>();
      const inputs: LocalOrRemoteMedia[] = [];

      if (session && filter(session)) {
        session.medias.forEach((media) => {
          const localMedia = studentCtx?.selfStreams?.[media.id];
          if (localMedia) {
            inputs.push({ type: "local", session, mediaSession: media, stream: localMedia });
            foundMedias.add(media.id);
          }
        });
      }

      receptionSessions?.forEach((session) => {
        if (!filter(session.data.session)) return;
        if (foundMedias.has(session.data.mediaSession.id)) return; // we alreay have the local version
        inputs.push({
          type: "remote",
          mediaSession: session.data.mediaSession,
          session: session.data.session,
          videoSession: session,
        });
        foundMedias.add(session.data.mediaSession.id);
      });

      return inputs;
    },
    [session, studentCtx, receptionSessions],
  );
}